import { Industry, TeamSize } from '@zond/api-interfaces';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@zond/ui';
import { Camera, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { brandedConfig } from '../../../utils/branding';
import { useStore } from '../../store';
import {
  useGetWorkspace,
  useUpdateWorkspace,
  useUpdateWorkspaceAvatar,
} from '../workspace/workspace.queries';

const teamSizes = [
  { value: TeamSize.to5, label: '1-5' },
  { value: TeamSize.to10, label: '6-10' },
  { value: TeamSize.to20, label: '11-20' },
  { value: TeamSize.to50, label: '21-50' },
  { value: TeamSize.to100, label: '51-100' },
  { value: TeamSize.to250, label: '101-250' },
  { value: TeamSize.to500, label: '251-500' },
  { value: TeamSize.to1000, label: '501-1000' },
  { value: TeamSize.moreThan1000, label: '1000+' },
];

const industries = [
  { value: Industry.agency, labelKey: 'agency' },
  { value: Industry.consulting, labelKey: 'consulting' },
  { value: Industry.technology, labelKey: 'technology' },
  { value: Industry.financialServices, labelKey: 'finance' },
  { value: Industry.healthcare, labelKey: 'healthcare' },
  { value: Industry.education, labelKey: 'education' },
  { value: Industry.retail, labelKey: 'retail' },
  { value: Industry.businessServices, labelKey: 'businessServices' },
];

export const Workspace = () => {
  const { t } = useTranslation('settings');
  const workspaceId = useStore((state) => state.getSelectedWorkspaceId());
  const { data: workspace, refetch } = useGetWorkspace(workspaceId!);
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    website: '',
    teamSize: TeamSize.to5,
    industry: Industry.agency,
  });

  useEffect(() => {
    if (workspace) {
      setFormData({
        name: workspace.name || '',
        company: workspace.Company?.companyName || '',
        website: workspace.Company?.website || '',
        teamSize: (workspace.teamSize as TeamSize) || TeamSize.to5,
        industry: (workspace.industry as Industry) || Industry.agency,
      });
    }
  }, [workspace]);

  const updateWorkspace = useUpdateWorkspace(
    () => {
      refetch();
      toast.success(t('workspace.toasts.updated'));
    },
    () => {
      toast.error(t('workspace.toasts.updateFailed'));
    }
  );

  const updateAvatar = useUpdateWorkspaceAvatar(
    () => {
      refetch();
      toast.success(t('workspace.toasts.avatarUpdated'));
      setIsUploading(false);
    },
    () => {
      toast.error(t('workspace.toasts.avatarUpdateFailed'));
      setIsUploading(false);
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId) return;
    updateWorkspace.mutate({
      workspaceId,
      data: formData,
    });
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field: string) => (value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 1000000) {
      toast.error(t('workspace.toasts.avatarTooLarge'));
      return;
    }

    setIsUploading(true);
    updateAvatar.mutate(file);
  };

  return (
    <div className="container max-w-4xl py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">{t('workspace.title')}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="group relative">
                  <Avatar className="h-10 w-10 rounded-lg">
                    <AvatarImage src={workspace?.avatar ?? undefined} className="rounded-lg" />
                    <AvatarFallback className="rounded-lg">
                      <span role="img" aria-label="workspace" className="text-2xl">
                        🏢
                      </span>
                    </AvatarFallback>
                  </Avatar>
                  <label
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    htmlFor="avatar-upload"
                  >
                    {isUploading ? (
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    ) : (
                      <Camera className="h-6 w-6 text-white" />
                    )}
                  </label>
                  <input
                    type="file"
                    id="avatar-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    disabled={isUploading}
                  />
                </div>
                <div>
                  <CardTitle>{t('workspace.card.title')}</CardTitle>
                  <CardDescription>{t('workspace.card.description')}</CardDescription>
                </div>
              </div>
              <Button type="submit" disabled={updateWorkspace.isPending} className="min-w-[120px]">
                {updateWorkspace.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common:states.saving')}
                  </>
                ) : (
                  t('common:actions.save')
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('workspace.fields.name.label')}
                  <span className="text-sm text-muted-foreground block">
                    {t('workspace.fields.name.hint')}
                  </span>
                </Label>
                <Input id="name" value={formData.name} onChange={handleInputChange('name')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">
                  {t('workspace.fields.company.label')}
                  <span className="text-sm text-muted-foreground block">
                    {t('workspace.fields.company.hint')}
                  </span>
                </Label>
                <Input id="company" value={formData.company} onChange={handleInputChange('company')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">
                  {t('workspace.fields.website.label')}
                  <span className="text-sm text-muted-foreground block">
                    {t('workspace.fields.website.hint')}
                  </span>
                </Label>
                <Input
                  id="website"
                  type="text"
                  value={formData.website}
                  onChange={handleInputChange('website')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="teamSize">
                  {t('workspace.fields.teamSize.label')}
                  <span className="text-sm text-muted-foreground block">
                    {t('workspace.fields.teamSize.hint', { productName: brandedConfig.productName })}
                  </span>
                </Label>
                <Select value={formData.teamSize} onValueChange={handleSelectChange('teamSize')}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('workspace.fields.teamSize.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {teamSizes.map((size) => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="industry">
                  {t('workspace.fields.industry.label')}
                  <span className="text-sm text-muted-foreground block">
                    {t('workspace.fields.industry.hint')}
                  </span>
                </Label>
                <Select value={formData.industry} onValueChange={handleSelectChange('industry')}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('workspace.fields.industry.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry.value} value={industry.value}>
                        {t(`workspace.industries.${industry.labelKey}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default Workspace;
